import * as React from "react";
import {
  TrackerPolicy,
  TrackerPolicyContentStatusOptions,
  TrackerPolicyContentTypeOptions
} from "@models/tracker-policy";
import {Modal, Table, Button, Divider, Avatar, Tooltip} from "antd";
import {FormattedMessage, injectIntl, intlShape} from "react-intl";
import FormattedDateTime from "@components/custom/FormattedDateTime";
import Message from "@constants/message";
import {PaginationDefaultParams} from "../custom/Paginator";
import {NavLink} from "react-router-dom";
import {Icon} from 'antd';
import __i18n from "../../intl/__i18n";
import URLMaker from "./URLMaker";

type Props = {
  data: TrackerPolicy[];
  loading: boolean;
  deleteFn: (item: TrackerPolicy) => void;
  intl: intlShape.isRequired;
  currentPage: number;
};

const TrackerPoliciesTable: React.FC<Props> = props => {
  const {data, loading, currentPage, deleteFn, intl} = props;
  const {formatMessage} = intl;
  const pageOffset = (currentPage - 1) * PaginationDefaultParams.count;
  
  const deleteTrackerPolicy = (item: TrackerPolicy): void => {
    Modal.confirm({
      content: formatMessage({
        id: "app.general_delete_warning",
        defaultMessage: Message.Warning.Delete
      }),
      okText: formatMessage({
        id: "app.delete",
        defaultMessage: "Delete"
      }),
      cancelText: formatMessage({
        id: "app.cancel",
        defaultMessage: "Cancel"
      }),
      className: "delete-confirm-dialog",
      onOk: () => deleteFn(item)
    });
  };
  
  const columns = [
    {
      title: <FormattedMessage id="app.index" defaultMessage={`Index`}/>,
      width: 30,
      dataIndex: "id",
      key: "id",
      align: "right",
      render: (a, b, i) => <span>{i + 1 + pageOffset}</span>
    },
    {
      title: __i18n("Code"),
      width: 50,
      dataIndex: "short_id",
      key: "short_id",
      align: "right",
    },
    {
      title: <FormattedMessage id="app.title" defaultMessage={`Title`}/>,
      dataIndex: "title",
      align: "right",
      key: "title",
    },
    {
      title: <FormattedMessage id="app.start_track_date" defaultMessage={`Start track date`}/>,
      align: "center",
      dataIndex: "start_track_date_time",
      key: "start_track_date_time",
      render: (text, record) => <FormattedDateTime date={text}/>
    },
    {
      title: <FormattedMessage id="app.expiration_date" defaultMessage={`Expiration Date`}/>,
      align: "center",
      dataIndex: "expiration_date",
      key: "expiration_date",
      render: (value, record) => !!value ? <FormattedDateTime date={value}/> : "-"
    },
    {
      title: <FormattedMessage id="app.used_count" defaultMessage={`Used Count`}/>,
      dataIndex: "used_count",
      key: "used_count",
      align: "center",
    },
    {
      title: "",
      key: "actions",
      align: "left",
      dataIndex: "actions",
      render: (text, record) => (
        <span>
           <Tooltip title={<FormattedMessage id="app.edit" defaultMessage={`Edit`}/>}>
             <NavLink to={`/tracker-policies/${record.id}/edit`}>
               <Button type="link" size={`small`}>
                 <Icon type="edit"/>
              </Button>
             </NavLink>
          </Tooltip>

          <Divider type="vertical" size={`small`}/>
         
          <Tooltip title={<FormattedMessage id="app.delete" defaultMessage={`Delete`}/>}>
             <Button
               type="link"
               className={`danger`}
               onClick={() => deleteTrackerPolicy(record)}
             >
            <Icon type="delete"/>
            </Button>
          </Tooltip>
          
          <Divider type="vertical" size={`small`}/>
          
          <Tooltip title={<FormattedMessage id="app.click_count_report" defaultMessage={`Click count report`}/>}>
            <NavLink to={`/tracker-policies/${record.id}/report`}>
              <Button type="link" size={`small`}>
                <Icon type="area-chart"/>
              </Button>
            </NavLink>
          </Tooltip>
          
          <Divider type="vertical" size={`small`}/>
          
          <URLMaker code={record.short_id}/>
        </span>
      )
    }
  ];
  
  return (
    <Table
      columns={columns}
      loading={loading}
      pagination={false}
      dataSource={data}
      rowKey="id"
    />
  );
};

export default injectIntl(TrackerPoliciesTable);
